<template>
  <div class="flex1">
    <div class="row">
      <div class="col-md-12 col-12">
        <p>
          <span class="pointer" @click="goBack()" style="color:#fff">&larr; Back to catalog</span>
        </p>
        <h3>Promotional Offers</h3>
        <p>Product: <strong>{{ $route.params.productId }}</strong></p>

        <div v-if="!product" class="alert alert-danger">
          Product not found in catalog. Go back and open this page from a product row.
        </div>

        <div v-else>
          <div class="row mb-2">
            <div class="col-md-3 col-12">
              <label>Offer Identifier</label>
              <input class="form-control" type="text" v-model.trim="newOffer.offer_identifier"
                placeholder="App Store Connect offer id" />
            </div>
            <div class="col-md-3 col-12">
              <label>Offer Type</label>
              <select v-model="newOffer.offer_type" class="form-control">
                <option disabled value="">Please select an offer type</option>
                <option v-for="type in offerTypes" :key="'offerType-' + type.value" :value="type.value">
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div class="col-md-2 col-12" v-if="newOffer.offer_type && newOffer.offer_type !== 'FREE_TRIAL'">
              <label>Price</label>
              <input class="form-control" type="number" v-model="newOffer.price" />
            </div>
            <div class="col-md-2 col-12" v-if="newOffer.offer_type === 'PAY_UP_FRONT' || newOffer.offer_type === 'FREE_TRIAL'">
              <label>Period</label>
              <select v-model="newOffer.duration" class="form-control">
                <option disabled value="">Please select a period</option>
                <option v-for="duration in availablePeriods" :key="'promoDur-' + duration">
                  {{ duration }}
                </option>
              </select>
            </div>
            <div class="col-md-2 col-12" v-if="newOffer.offer_type === 'PAY_AS_YOU_GO'">
              <label>
                Number of periods
                <span class="info-tip">
                  <i class="fas fa-info-circle"></i>
                  <span class="info-tip-text">How many times the product's period repeats (1-12). The period is locked to the product's own duration ({{ (product && product.product_duration) || "unset" }}).</span>
                </span>
              </label>
              <select v-model.number="newOffer.number_of_periods" class="form-control">
                <option v-for="n in 12" :key="'periods-' + n" :value="n">{{ n }}</option>
              </select>
            </div>
            <div class="col-md-2 col-12">
              <label>
                Source
                <span class="info-tip">
                  <i class="fas fa-info-circle"></i>
                  <span class="info-tip-text">Where this offer will be redeemed from</span>
                </span>
              </label>
              <select v-model="newOffer.source" class="form-control">
                <option disabled value="">Please select a source</option>
                <option v-for="src in sources" :key="'source-' + src.value" :value="src.value">
                  {{ src.label }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="offerError" class="alert alert-danger">{{ offerError }}</div>

          <div class="mb-3">
            <button class="btn btn-success btn-sm mx-1" @click="submitOffer()">Save offer</button>
            <button v-if="editing" class="btn btn-secondary btn-sm mx-1" @click="resetForm()">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-12 col-12" v-if="product">
      <h3>Current Offers</h3>
      <div class="card row py-2">
        <div class="col-12">
          <div class="row">
            <div class="col-3"><p>Offer Identifier</p></div>
            <div class="col-2"><p>Type</p></div>
            <div class="col-1"><p>Price</p></div>
            <div class="col-1"><p>Duration</p></div>
            <div class="col-1"><p>Periods</p></div>
            <div class="col-2"><p>Source</p></div>
            <div class="col-2"><p>Actions</p></div>
          </div>
        </div>
        <div class="col-12">
          <div class="row" v-for="[offerId, offer] in offers" :key="offerId">
            <div class="col-3"><p>{{ offerId }}</p></div>
            <div class="col-2"><p>{{ offerTypeLabel(offer.offer_type) }}</p></div>
            <div class="col-1"><p v-if="offer.offer_type !== 'FREE_TRIAL'">${{ offer.price }}</p></div>
            <div class="col-1"><p>{{ periodDisplay(offer) }}</p></div>
            <div class="col-1"><p>{{ offer.number_of_periods || 1 }}</p></div>
            <div class="col-2"><p>{{ sourceLabel(offer.source) }}</p></div>
            <div class="col-2">
              <button class="btn btn-info btn-sm mr-3" @click="editOffer(offerId, offer)">Edit</button>
              <button class="btn btn-danger btn-sm" @click="removeOffer(offerId)">DEL</button>
            </div>
          </div>
          <div class="row" v-if="offers.length === 0">
            <div class="col-12"><p>No promotional offers yet.</p></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      newOffer: { offer_identifier: "", offer_type: "", price: undefined, duration: "", number_of_periods: 1, source: "" },
      editing: false,
      offerError: "",
      offerTypes: [
        { value: "PAY_AS_YOU_GO", label: "Pay as you go" },
        { value: "PAY_UP_FRONT", label: "Pay up front" },
        { value: "FREE_TRIAL", label: "Free trial" },
      ],
      sources: [
        { value: "RETENTION_MESSAGING", label: "Retention Messaging" },
        { value: "CLIENT", label: "Client" },
        { value: "WIN-BACK_OFFER", label: "Win-back Offer" },
      ],
      // Allowed base periods for the single-period offer types, per Apple's rules.
      // PAY_AS_YOU_GO has no period here — it's locked to the product's own
      // duration; it only configures number_of_periods (how many times it repeats).
      periodsByType: {
        FREE_TRIAL: ["3 Days", "1 Week", "2 Weeks", "1 Month", "2 Months", "3 Months", "6 Months", "1 Year"],
        PAY_UP_FRONT: ["1 Month", "2 Months", "3 Months", "6 Months", "1 Year"],
      },
    };
  },
  computed: {
    catalog() {
      return this.$store.getters.catalog;
    },
    product() {
      return this.catalog.find((p) => p.product_id === this.$route.params.productId);
    },
    offers() {
      return Object.entries((this.product && this.product.promotional_offers) || {});
    },
    availablePeriods() {
      return this.periodsByType[this.newOffer.offer_type] || [];
    },
  },
  watch: {
    "newOffer.offer_type"() {
      // Clear a period that isn't valid for the newly selected offer type, and
      // reset the repeat count for types that don't repeat.
      if (!this.availablePeriods.includes(this.newOffer.duration)) {
        this.newOffer.duration = "";
      }
      if (this.newOffer.offer_type !== "PAY_AS_YOU_GO") {
        this.newOffer.number_of_periods = 1;
      }
    },
  },
  methods: {
    offerTypeLabel(value) {
      const match = this.offerTypes.find((t) => t.value === value);
      return match ? match.label : value;
    },
    sourceLabel(value) {
      const match = this.sources.find((s) => s.value === value);
      return match ? match.label : value;
    },
    periodDisplay(offer) {
      // PAY_AS_YOU_GO has no stored period — it's the product's own duration.
      if (offer.offer_type === "PAY_AS_YOU_GO") {
        return (this.product && this.product.product_duration) || "(product)";
      }
      return offer.duration;
    },
    submitOffer() {
      this.offerError = "";
      const { offer_identifier, offer_type, price, duration, number_of_periods, source } = this.newOffer;
      if (!offer_identifier || !offer_type || !source) {
        this.offerError = "Offer identifier, offer type and source are required.";
        return;
      }
      if (offer_type !== "FREE_TRIAL" && (price === undefined || price === "" || isNaN(Number(price)))) {
        this.offerError = "Price is required and must be numeric for this offer type.";
        return;
      }
      const payload = {
        product_id: this.$route.params.productId,
        offer_identifier,
        offer_type,
        source,
        price: offer_type === "FREE_TRIAL" ? undefined : Number(price),
      };
      if (offer_type === "PAY_AS_YOU_GO") {
        // Period is locked to the product's duration; only the repeat count is sent.
        const periods = Number(number_of_periods);
        if (!Number.isInteger(periods) || periods < 1 || periods > 12) {
          this.offerError = "Number of periods must be an integer between 1 and 12.";
          return;
        }
        payload.number_of_periods = periods;
      } else {
        if (!duration) {
          this.offerError = "Period is required.";
          return;
        }
        payload.duration = duration;
      }
      this.$store.dispatch("updatePromotionalOffer", payload);
      this.resetForm();
    },
    editOffer(offerId, offer) {
      this.editing = true;
      this.offerError = "";
      this.newOffer = {
        offer_identifier: offerId,
        offer_type: offer.offer_type,
        price: offer.price,
        duration: offer.duration,
        number_of_periods: offer.number_of_periods || 1,
        source: offer.source || "",
      };
    },
    removeOffer(offerId) {
      if (!confirm(`Are you sure you want to delete promotional offer "${offerId}"?`)) {
        return;
      }
      this.$store.dispatch("deletePromotionalOffer", {
        product_id: this.$route.params.productId,
        offer_identifier: offerId,
      });
    },
    resetForm() {
      this.newOffer = { offer_identifier: "", offer_type: "", price: undefined, duration: "", number_of_periods: 1, source: "" };
      this.editing = false;
      this.offerError = "";
    },
    goBack() {
      this.$router.push({ name: "CatalogManager", params: { id: this.$route.params.id } });
    },
  },
};
</script>

<style scoped>
.info-tip {
  position: relative;
  display: inline-block;
  margin-left: 4px;
  cursor: help;
}
.info-tip i {
  opacity: 0.7;
}
.info-tip .info-tip-text {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e1e2f;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: normal;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: opacity 0.15s ease-in-out;
}
.info-tip:hover .info-tip-text {
  visibility: visible;
  opacity: 1;
}
</style>
